import type { SnapshotRow } from '@cortex/styling-pattern-watch-core';
import type { CategoryDef } from '../dashboard-types.js';

/** @graph-connects none */
export type HistoryRange = 'today' | '7d' | '14d' | '30d';

export interface HistoryBucket {
  /** x 軸ラベル。today: "HH:00" JST、daily: "MM/DD" JST */
  label: string;
  /** snapshot_taken_at（ISO）。サンプルが無いバケットでは null */
  isoTimestamp: string | null;
}

export interface HistoryCategorySeries {
  categoryId: number;
  /** バケット数と同じ長さ。サンプルが無いバケットは 0 */
  values: number[];
}

export interface HistoryChartData {
  buckets: HistoryBucket[];
  series: HistoryCategorySeries[];
  /** 取得行が 0 件 / 全バケット 0 のとき true。Web 側で空状態 UI を出すため */
  isEmpty: boolean;
}

type CategorySnapshotRow = SnapshotRow & { category_id: number; ac_size: null; md_taste: null };

/** @graph-connects none */
const DAY_RANGE_LENGTH: Record<Exclude<HistoryRange, 'today'>, number> = {
  '7d': 7,
  '14d': 14,
  '30d': 30,
};

/** @graph-connects none */
const TODAY_HOURS_JST = [0, 3, 6, 9, 12, 15, 18, 21];

/**
 * @graph-stack styling-pattern-watch
 * @graph-domain Styling
 * @graph-business /v1/snapshots level=category の行から HistoryView の time-series チャート用データを組み立てる。今日ビューは JST 0/3/.../21 時、日次ビューは末尾を today JST として N 日分（最新スナップショットを採用）
 * @graph-connects none
 */
export function buildHistoryChartData(input: {
  rows: SnapshotRow[];
  range: HistoryRange;
  categories: CategoryDef[];
  now: Date;
}): HistoryChartData {
  const buckets = buildBuckets(input.range, input.now);
  const categoryIds = input.categories.map((c) => c.id);
  const filtered = input.rows.filter(
    (r): r is CategorySnapshotRow =>
      r.category_id !== null && r.ac_size === null && r.md_taste === null,
  );

  const series = categoryIds.map((id) => ({
    categoryId: id,
    values: buckets.map(() => 0),
  }));
  let nonZero = 0;

  if (input.range === 'today') {
    const todayKey = jstDateKey(input.now);
    const todaysRows = filtered.filter((r) => jstDateKeyFromIso(r.snapshot_taken_at) === todayKey);
    const isoByHour = new Map<number, string>();
    const valueByHourCategory = new Map<string, number>();
    for (const row of todaysRows) {
      const hour = jstHourFromIso(row.snapshot_taken_at);
      isoByHour.set(hour, row.snapshot_taken_at);
      valueByHourCategory.set(`${String(hour)}:${String(row.category_id)}`, row.pattern_count);
    }
    buckets.forEach((bucket, i) => {
      const hour = TODAY_HOURS_JST[i];
      const isoForHour = isoByHour.get(hour);
      if (isoForHour) bucket.isoTimestamp = isoForHour;
      series.forEach((s) => {
        const v = valueByHourCategory.get(`${String(hour)}:${String(s.categoryId)}`) ?? 0;
        s.values[i] = v;
        if (v > 0) nonZero += 1;
      });
    });
  } else {
    const latestPerDay = pickLatestPerJstDate(filtered);
    buckets.forEach((bucket, i) => {
      const dateKey = bucket.label;
      const dayRows = latestPerDay.get(dateKey);
      if (!dayRows) return;
      bucket.isoTimestamp = dayRows.iso;
      for (const row of dayRows.rows) {
        const seriesEntry = series.find((s) => s.categoryId === row.category_id);
        if (!seriesEntry) continue;
        seriesEntry.values[i] = row.pattern_count;
        if (row.pattern_count > 0) nonZero += 1;
      }
    });
  }

  const trimmedLength = trailingBucketsWithData(buckets);
  if (trimmedLength < buckets.length) {
    const trimmedBuckets = buckets.slice(0, trimmedLength);
    const trimmedSeries = series.map((s) => ({
      categoryId: s.categoryId,
      values: s.values.slice(0, trimmedLength),
    }));
    return { buckets: trimmedBuckets, series: trimmedSeries, isEmpty: nonZero === 0 };
  }
  return { buckets, series, isEmpty: nonZero === 0 };
}

/**
 * 末尾の「データが届いていないバケット」を取り除き、未来の時間にライン
 * グラフが 0 まで急降下するアーティファクトを抑える。最後にデータが揃っ
 * たバケットの index+1 を返す。
 * @graph-connects none
 */
function trailingBucketsWithData(buckets: HistoryBucket[]): number {
  for (let i = buckets.length - 1; i >= 0; i -= 1) {
    if (buckets[i]?.isoTimestamp !== null) return i + 1;
  }
  return 0;
}

/** @graph-connects none */
function buildBuckets(range: HistoryRange, now: Date): HistoryBucket[] {
  if (range === 'today') {
    return TODAY_HOURS_JST.map((h) => ({
      label: `${String(h).padStart(2, '0')}:00`,
      isoTimestamp: null,
    }));
  }
  const days = DAY_RANGE_LENGTH[range];
  const buckets: HistoryBucket[] = [];
  for (let i = days - 1; i >= 0; i -= 1) {
    const dateKey = jstDateKey(addDays(now, -i));
    buckets.push({ label: jstShortDateLabel(dateKey), isoTimestamp: null });
  }
  return buckets;
}

interface DaySnapshot {
  iso: string;
  rows: SnapshotRow[];
}

/** @graph-connects none */
function pickLatestPerJstDate(rows: SnapshotRow[]): Map<string, DaySnapshot> {
  // snapshot_taken_at で日ごとに最新のタイムスタンプを選び、その時刻の行群を集める
  const latestIsoByDate = new Map<string, string>();
  for (const row of rows) {
    const dateKey = jstDateKey(new Date(row.snapshot_taken_at));
    const existing = latestIsoByDate.get(dateKey);
    if (existing === undefined || row.snapshot_taken_at > existing) {
      latestIsoByDate.set(dateKey, row.snapshot_taken_at);
    }
  }
  const result = new Map<string, DaySnapshot>();
  for (const [dateKey, iso] of latestIsoByDate) {
    const dayRows = rows.filter((r) => r.snapshot_taken_at === iso);
    result.set(jstShortDateLabel(dateKey), { iso, rows: dayRows });
  }
  return result;
}

/** @graph-connects none */
function jstDateKey(date: Date): string {
  const jst = new Date(date.getTime() + 9 * 60 * 60 * 1000);
  const y = jst.getUTCFullYear();
  const m = String(jst.getUTCMonth() + 1).padStart(2, '0');
  const d = String(jst.getUTCDate()).padStart(2, '0');
  return `${String(y)}-${m}-${d}`;
}

/** @graph-connects none */
function jstDateKeyFromIso(iso: string): string {
  return jstDateKey(new Date(iso));
}

/** @graph-connects none */
function jstHourFromIso(iso: string): number {
  const jst = new Date(new Date(iso).getTime() + 9 * 60 * 60 * 1000);
  return jst.getUTCHours();
}

/** @graph-connects none */
function jstShortDateLabel(dateKey: string): string {
  return `${dateKey.slice(5, 7)}/${dateKey.slice(8, 10)}`;
}

/** @graph-connects none */
function addDays(date: Date, days: number): Date {
  return new Date(date.getTime() + days * 24 * 60 * 60 * 1000);
}

/**
 * @graph-stack styling-pattern-watch
 * @graph-domain Styling
 * @graph-business 指定 range の /v1/snapshots クエリ用 fromIso を JST 基準で算出する。today: JST 当日 00:00 / Nd: JST (N-1) 日前 00:00
 * @graph-connects none
 */
export function rangeFromIso(range: HistoryRange, now: Date): string {
  if (range === 'today') {
    return jstStartOfDayUtcIso(now);
  }
  const days = DAY_RANGE_LENGTH[range];
  return jstStartOfDayUtcIso(addDays(now, -(days - 1)));
}

/** @graph-connects none */
function jstStartOfDayUtcIso(date: Date): string {
  const jstMidnightMillis = new Date(jstDateKey(date) + 'T00:00:00+09:00').getTime();
  return new Date(jstMidnightMillis).toISOString();
}
